/**
 * Coast Investment Terminal — First-Login Verification Gauntlet (WebAuthn)
 *
 * Rendered between successful password auth and a real session for any user
 * whose backend `verified_at` is NULL (freshly accepted invites, or accounts
 * an admin has reset). Two sequential steps:
 *
 *   1. Email code   — 6-digit code from the confirmation email
 *   2. Passkey      — enroll a passkey via Touch ID / Face ID / Windows Hello
 *                     / PIN / roaming device (QR-to-phone) / security key
 *
 * On success, the passkey registration endpoint returns a full access token
 * and we hand it back to App.tsx via onLogin().
 *
 * Design note
 * -----------
 * This component used to run an authenticator-app (TOTP) step as step 2.
 * That was replaced on 2026-04-18 because iOS was intercepting the QR code
 * and the UX was broken for users who don't already live in an authenticator
 * app. Passkeys use whatever secure unlock mechanism the user's device
 * already has, which is dramatically simpler. See backend/routers/webauthn_auth.py.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { startRegistration } from '@simplewebauthn/browser';

const API = 'https://coast-terminal-production.up.railway.app';

interface Props {
  preauthToken: string;
  emailHint?: string;
  name?: string;
  onLogin: (token: string, name?: string, role?: string) => void;
  onCancel: () => void;
}

type Step = 'email' | 'passkey' | 'done';

export default function VerifyFirstLoginPage({
  preauthToken: initialPreauth,
  emailHint,
  name,
  onLogin,
  onCancel,
}: Props) {
  const [step, setStep] = useState<Step>('email');
  const [preauth, setPreauth] = useState(initialPreauth);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Email step state
  const [code, setCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // ── Step 1: confirm email code ─────────────────────────────────────────────
  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6 || loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${API}/verify/email/confirm`,
        { code },
        { headers: { Authorization: `Bearer ${preauth}` } },
      );
      // For first-login (verified_at was NULL) the server advances us to
      // the passkey step. For the email-fallback case (verified_at already
      // set) it would return an access_token directly — but that branch
      // only runs in the regular Login flow, not here.
      if (res.data.access_token) {
        setStep('done');
        onLogin(res.data.access_token, res.data.name, res.data.role);
        return;
      }
      setPreauth(res.data.preauth_token);
      setStep('passkey');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Incorrect code.');
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/verify/email/resend`, {}, {
        headers: { Authorization: `Bearer ${preauth}` },
      });
      setResendCooldown(60);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Could not resend.';
      setError(msg);
      const m = /wait (\d+)s/.exec(msg);
      if (m) setResendCooldown(parseInt(m[1], 10));
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: enroll a passkey ──────────────────────────────────────────────
  //
  // The browser's native prompt does all the work — Touch ID on a MacBook,
  // Face ID on an iPhone, Windows Hello on a PC, or a QR-to-phone flow if
  // the desktop has no biometrics. We just hand the options to
  // startRegistration() and ship the attestation back to the server.
  const enrollPasskey = async () => {
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      // 1. Ask the server for registration options + a signed challenge.
      const beginRes = await axios.post(
        `${API}/webauthn/register/begin`,
        {},
        { headers: { Authorization: `Bearer ${preauth}` } },
      );
      const { options, challenge_token } = beginRes.data;

      // 2. Invoke the browser — this triggers the OS unlock prompt.
      const credential = await startRegistration({ optionsJSON: options });

      // 3. Send the signed attestation back. On first-login the server
      //    also marks verified_at and returns a full access token.
      const finishRes = await axios.post(
        `${API}/webauthn/register/finish`,
        {
          challenge_token,
          credential,
          device_name: _guessDeviceName(),
        },
        { headers: { Authorization: `Bearer ${preauth}` } },
      );

      if (finishRes.data.access_token) {
        setStep('done');
        onLogin(finishRes.data.access_token, finishRes.data.name, finishRes.data.role);
      } else {
        setError('Passkey saved, but no access token returned. Please sign in again.');
      }
    } catch (err: any) {
      // startRegistration() throws for user-cancelled prompts too.
      const msg = err?.response?.data?.detail
        || (err?.name === 'NotAllowedError' ? 'Enrollment was cancelled or timed out.' : null)
        || err?.message
        || 'Could not enroll passkey.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const bg = '#050E1A';
  const surface = '#0e1420';
  const border = '#1e2a3d';
  const gold = '#C9A96E';
  const goldLight = '#D4AF7A';
  const textPrimary = '#F0E8D8';
  const textSecondary = '#7A8CA0';
  const textMuted = '#3E5070';
  const serif = "'Fraunces', Georgia, serif";

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', fontFamily: "'EB Garamond', Georgia, serif" }}>
      <div style={{ width: '100%', maxWidth: '440px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '8px', fontFamily: serif, color: goldLight }}>COAST</div>
          <div style={{ fontSize: '10px', color: textMuted, letterSpacing: '2px', textTransform: 'uppercase', marginTop: '6px', fontWeight: 700 }}>First-Time Verification</div>
        </div>

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '28px' }}>
          {(['email', 'passkey'] as Step[]).map(s => (
            <span key={s} style={{
              width: step === s ? '28px' : '8px',
              height: '4px',
              borderRadius: '2px',
              backgroundColor: step === s ? gold : (step === 'passkey' && s === 'email' ? gold : border),
              transition: 'all 0.3s',
            }} />
          ))}
        </div>

        <div style={{ backgroundColor: surface, border: `1px solid ${border}`, padding: '32px' }}>

          {/* ── Email step ── */}
          {step === 'email' && (
            <form onSubmit={submitCode}>
              <div style={{ fontSize: '22px', fontWeight: 300, fontFamily: serif, color: textPrimary, marginBottom: '6px' }}>
                Confirm it's you{name ? `, ${name}` : ''}.
              </div>
              <div style={{ fontSize: '13px', color: textSecondary, marginBottom: '24px', lineHeight: 1.6 }}>
                We sent a 6-digit code to <strong style={{ color: textPrimary }}>{emailHint || 'your email'}</strong>. It expires in 10 minutes.
              </div>

              <input
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                autoFocus
                style={{
                  width: '100%', boxSizing: 'border-box', backgroundColor: '#1a2438',
                  border: `1px solid ${border}`, borderRadius: '3px', padding: '16px',
                  color: textPrimary, fontSize: '28px', letterSpacing: '12px',
                  textAlign: 'center', outline: 'none',
                  fontFamily: "'Courier New', monospace", marginBottom: '16px',
                }}
              />

              {error && <ErrorBox>{error}</ErrorBox>}

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                style={buttonStyle(code.length === 6 && !loading, gold, bg, textMuted)}
              >
                {loading ? 'Verifying…' : 'Verify Code'}
              </button>

              <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                <span onClick={onCancel} style={{ color: textMuted, cursor: 'pointer', fontWeight: 600 }}>
                  ← Back to sign in
                </span>
                <span
                  onClick={resendCode}
                  style={{
                    color: resendCooldown > 0 || loading ? textMuted : goldLight,
                    cursor: resendCooldown > 0 || loading ? 'default' : 'pointer',
                    fontWeight: 600,
                  }}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </span>
              </div>
            </form>
          )}

          {/* ── Passkey step ── */}
          {step === 'passkey' && (
            <div>
              <div style={{ fontSize: '22px', fontWeight: 300, fontFamily: serif, color: textPrimary, marginBottom: '6px' }}>
                Set up a passkey.
              </div>
              <div style={{ fontSize: '13px', color: textSecondary, marginBottom: '20px', lineHeight: 1.6 }}>
                Your passkey replaces authenticator apps and text codes. When you click below, your device will ask you to confirm with{' '}
                <strong style={{ color: textPrimary }}>Touch ID, Face ID, Windows Hello, or a PIN</strong>. If none of those are
                available, it will offer to pair with your phone via a one-time QR scan.
              </div>

              <ul style={{ fontSize: '12px', color: textSecondary, lineHeight: 1.9, paddingLeft: '18px', margin: '0 0 20px 0' }}>
                <li>Works across devices signed into the same iCloud / Google account.</li>
                <li>No app to install. No codes to type.</li>
                <li>You can still use an email code if your device is unavailable.</li>
              </ul>

              {error && <ErrorBox>{error}</ErrorBox>}

              <button
                type="button"
                onClick={enrollPasskey}
                disabled={loading}
                style={buttonStyle(!loading, gold, bg, textMuted)}
              >
                {loading ? 'Waiting for device…' : 'Enroll Passkey'}
              </button>
            </div>
          )}

          {step === 'done' && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: textPrimary, fontSize: '14px' }}>
              Signing you in…
            </div>
          )}
        </div>

        <div style={{ marginTop: '24px', fontSize: '10px', color: textMuted, textAlign: 'center', letterSpacing: '1px', textTransform: 'uppercase' }}>
          Proprietary · Coast Investment Terminal
        </div>
      </div>
    </div>
  );
}

// Best-effort device label so the credentials list shows "Jacob's MacBook"
// instead of just "Passkey". Purely cosmetic.
function _guessDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Passkey';
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      backgroundColor: 'rgba(204,34,0,0.08)',
      border: '1px solid rgba(204,34,0,0.25)',
      borderLeft: '3px solid #CC2200',
      borderRadius: '3px',
      padding: '11px 14px',
      marginBottom: '16px',
      fontSize: '12px',
      color: '#E07070',
      lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
}

function buttonStyle(active: boolean, gold: string, bg: string, textMuted: string): React.CSSProperties {
  return {
    width: '100%',
    padding: '14px',
    backgroundColor: active ? gold : 'rgba(15,32,56,0.8)',
    color: active ? bg : textMuted,
    border: 'none',
    borderRadius: '3px',
    fontWeight: 800,
    fontSize: '11px',
    letterSpacing: '2.5px',
    textTransform: 'uppercase',
    cursor: active ? 'pointer' : 'default',
    fontFamily: 'inherit',
  };
}
